import React, { useEffect, useMemo, useState } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import {
	Gallery,
	type Image,
	type ThumbnailImageProps,
} from 'react-grid-gallery'; // Added Image type and ThumbnailImageProps
// Import Icons
import {
	FaLock,
	FaLockOpen,
	FaRedo,
	FaTrash,
	FaUndo,
} from 'react-icons/fa'; // Replaced FaImage with FaStar

import useLightbox from '../../hooks/useLightbox';
import useMediaGallery from '../../hooks/useMediaGallery';
import type { MediaItem } from '../../hooks/useMediaGallery';
import useSimpleMutation from '../../hooks/useSimpleMutation'; // Added useSimpleMutation
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
} from '../Shared/CommonStyles';
// import { Button } from '../Shared/FormStyles'; // Added Button

import MediaLightbox from './MediaLightbox'; // Import the new component
import * as S from './Pictures.styles'; // Import styled components

// --- Thumbnail renderer (module scope) ---
// react-grid-gallery treats a recreated child function as a NEW component
// type, unmounting/remounting every thumbnail (state resets, spinners
// flash). Module scope keeps the identity stable; data flows in via
// context.
interface GalleryThumbContextValue {
	mediaItems: MediaItem[];
	pendingChanges: PendingChangesMap;
	isEditingPhotos: boolean;
	resolvedAlbumId?: string;
	onDeleteClick: (itemId: string) => void;
	onRotateClick: (itemId: string, direction: 'left' | 'right') => void;
	onToggleSecretClick: (itemId: string, currentSecret: boolean) => void;
}
const GalleryThumbContext =
	React.createContext<GalleryThumbContextValue | null>(null);

const GalleryCustomThumbnail: React.FC<ThumbnailImageProps> = ({
	item,
	index,
	imageProps,
}: ThumbnailImageProps) => {
	const [isLoaded, setIsLoaded] = useState(false);

	const ctx = React.useContext(GalleryThumbContext)!;

	// Get the original media item using the index to access its _id
	const mediaItem = ctx.mediaItems[index];
	const itemId = mediaItem?._id;
	const pendingChange = itemId
		? ctx.pendingChanges.get(itemId)
		: undefined;
	const isPendingDelete = pendingChange?.action === 'delete';
	// Lossless display rotation: pending value is a DELTA on top of the
	// persisted rotation field (which itself never modifies image bytes).
	const baseRotation = mediaItem?.rotation ?? 0;
	const pendingRotation =
		pendingChange?.action === 'rotate' ? (pendingChange.value as number) : 0;
	const totalRotation =
		(((baseRotation + pendingRotation) % 360) + 360) % 360;
	const isRotated90 = totalRotation === 90 || totalRotation === 270;
	const isPendingSecretToggle = pendingChange?.action === 'toggleSecret';

	// Determine final secret status
	const isSecret = mediaItem?.isSecret || false;
	const finalIsSecret = isPendingSecretToggle
		? pendingChange?.value
		: isSecret;

	// Clean imageProps to handle title: null -> undefined mismatch
	const cleanImageProps = {
		...imageProps,
		title: imageProps.title === null ? undefined : imageProps.title,
		onLoad: () => setIsLoaded(true), // Set loaded state to true when image loads
	};

	// Prevent rendering overlay if itemId is missing
	if (!itemId) {
		return (
			<img
				{...cleanImageProps}
				alt={item.alt || `Gallery item ${index}`}
			/>
		);
	}

	// Apply styles based on pending changes
	const wrapperStyle: React.CSSProperties = {};
	if (isPendingDelete) {
		wrapperStyle.opacity = 0.5;
	}

	const imageStyle: React.CSSProperties = {
		display: isLoaded ? 'block' : 'none', // Hide image until loaded
	};
	if (totalRotation) {
		// The rotated element must be CENTERED on the slot: rotation pivots
		// around the element's center, so a top-left-anchored element puts
		// the rotated visual off to the side (white gaps / clipped edges).
		// react-grid-gallery item has the SCALED slot metrics (row layout
		// may shave tiles to fit the row width), so size from those.
		const layoutItem = item as unknown as {
			viewportWidth?: number;
			scaledHeight?: number;
		};
		const slotW = layoutItem.viewportWidth ?? item.width;
		const slotH = layoutItem.scaledHeight ?? item.height;
		if (isRotated90) {
			// Element is the UNROTATED (landscape) box — wider than the
			// portrait slot by design; after centering + rotate(90/270)
			// its visual exactly fills the slot.
			imageStyle.position = 'absolute';
			imageStyle.top = '50%';
			imageStyle.left = '50%';
			imageStyle.width = slotH;
			imageStyle.height = slotW;
		} else {
			// 180deg: same size as the slot, still needs centering so the
			// rotation maps the corners correctly.
			imageStyle.position = 'absolute';
			imageStyle.top = '50%';
			imageStyle.left = '50%';
			imageStyle.width = '100%';
			imageStyle.height = '100%';
		}
		imageStyle.transform = `translate(-50%, -50%) rotate(${totalRotation}deg)`;
		imageStyle.transition = 'transform 0.3s ease';
	}

	return (
		<S.ThumbnailWrapper
			style={wrapperStyle}
			$isEditing={ctx.isEditingPhotos}
			$isPendingDelete={isPendingDelete}
		>
			{!isLoaded && (
				<LoadingMessage
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						margin: 0,
						padding: 0,
					}}
				>
					<LoadingGif size="large" />
				</LoadingMessage>
			)}
			<S.ThumbnailImage
				{...cleanImageProps}
				alt={item.alt || `Gallery item ${index}`}
				style={imageStyle}
			/>
			{/* Show lock icon if item is secret */}
			{finalIsSecret && !isPendingDelete && (
				<div
					style={{
						position: 'absolute',
						top: '5px',
						left: '5px',
						color: '#f39c12',
						fontSize: '1.2rem',
						zIndex: 5,
						background: 'rgba(0,0,0,0.5)',
						borderRadius: '50%',
						padding: '4px',
						display: 'flex',
					}}
				>
					<FaLock />
				</div>
			)}

			{ctx.isEditingPhotos && (
				<S.ThumbnailOverlayContainer>
					{isPendingDelete ? (
						<S.ThumbnailOverlayMessage>
							Marcat per eliminar
						</S.ThumbnailOverlayMessage>
					) : (
						<>
							<S.ThumbnailOverlayButton
								title="Eliminar"
								className="delete"
								onClick={() => ctx.onDeleteClick(itemId)}
								data-edit-button="true"
							>
								<FaTrash />
							</S.ThumbnailOverlayButton>
							<S.ThumbnailOverlayButton
								title="Girar Esquerra"
								className="rotate"
								onClick={() =>
									ctx.onRotateClick(itemId, 'left')
								}
								data-edit-button="true"
							>
								<FaUndo />
							</S.ThumbnailOverlayButton>
							<S.ThumbnailOverlayButton
								title="Girar Dreta"
								className="rotate"
								onClick={() =>
									ctx.onRotateClick(itemId, 'right')
								}
								data-edit-button="true"
							>
								<FaRedo />
							</S.ThumbnailOverlayButton>
							<S.ThumbnailOverlayButton
								title={
									finalIsSecret
										? 'Fer públic'
										: 'Fer secret'
								}
								className="rotate" // Reuse rotate style or add new one
								onClick={() =>
									ctx.onToggleSecretClick(
										itemId,
										isSecret
									)
								}
								data-edit-button="true"
							>
								{finalIsSecret ? <FaLockOpen /> : <FaLock />}
							</S.ThumbnailOverlayButton>
						</>
					)}
				</S.ThumbnailOverlayContainer>
			)}
			{pendingRotation !== 0 && !isPendingDelete && (
				<S.RotationIndicator>
					{pendingRotation}°
				</S.RotationIndicator>
			)}
		</S.ThumbnailWrapper>
	);
};



// Interfaces MediaItem, MediaList, GalleryImage are defined in hooks or should be moved to a shared types file

// Define the type for pending changes, can be shared
export type PendingChange = {
	action: 'delete' | 'rotate' | 'toggleSecret';
	value?: number | boolean | string;
};
export type PendingChangesMap = Map<string, PendingChange>;

interface PicturesProps {
	albumId?: string;
	unitat?: string;
	page?: number;
	pageSize?: number;
	sortField?: string; // Add sortField
	sortDirection?: string; // Add sortDirection
	minYear?: string; // School year filter (e.g., "2023-2024")
	maxYear?: string; // School year filter (e.g., "2023-2024")
	onPageChange?: (page: number) => void;
	onPageCountChange?: (count: number) => void;
	onSortFieldChange?: (field: string) => void; // Add handler
	onSortDirectionChange?: (direction: string) => void; // Add handler
	// Props for editing managed by parent
	canEdit?: boolean;
	isEditingPhotos?: boolean;
	pendingChanges: PendingChangesMap; // Receive pending changes from parent
	onPendingChange: (itemId: string, change: PendingChange | null) => void; // Callback to update parent state
	// setIsEditingPhotos?: React.Dispatch<React.SetStateAction<boolean>>; // Removed, parent handles confirmation
	onIsLoadingChange?: (isLoading: boolean) => void; // New prop
	showSecret?: boolean; // New prop to indicate if we are showing secret items (e.g. in secret tab)
	includeSecret?: boolean; // Prop to include secret items in results
	hideAlbumName?: boolean; // Prop to hide album name in lightbox
}

const Pictures: React.FC<PicturesProps> = ({
	albumId,
	unitat,
	page = 1,
	pageSize,
	sortField: initialSortField, // Rename for clarity
	sortDirection: initialSortDirection,
	minYear,
	maxYear,
	onPageChange,
	onPageCountChange,
	onSortFieldChange,
	onSortDirectionChange,
	// Destructure new props
	// canEdit = false, // Default to false if not provided
	isEditingPhotos = false,
	pendingChanges, // Use prop
	onPendingChange, // Use prop
	onIsLoadingChange, // Destructure new prop
	showSecret, // Destructure new prop
	includeSecret, // Destructure new prop
	hideAlbumName, // Destructure new prop
}) => {
	const [localShowSecret, setLocalShowSecret] = useState(false);
	const effectiveShowSecret = showSecret || localShowSecret;

	// Raw per-item pending rotation DELTAS (±90), available before the
	// gallery hook resolves the picture list (which the combined-rotation
	// memo below needs). The hook combines delta + persisted rotation.
	const pendingDeltas = useMemo(() => {
		const m = new Map<string, number>();
		if (!pendingChanges) return m;
		for (const [itemId, change] of pendingChanges.entries()) {
			if (change.action !== 'rotate') continue;
			m.set(itemId, change.value as number);
		}
		return m;
	}, [pendingChanges]);

	// Use the useMediaGallery hook to handle data fetching, state, and handlers
	const {
		pictureList,
		isLoading, // Use isLoading from the hook
		error,
		thumbnailImages,
		handlePageChange: internalHandlePageChange,
		albumId: resolvedAlbumId, // Get resolved albumId from hook
		paramUnitat, // Get resolved unitat from hook (needed for query invalidation)
		page: currentPage, // Get current page from hook for query invalidation
		pageSize: currentPageSize // Get current page size from hook for query invalidation
	} = useMediaGallery({
		initialAlbumId: albumId,
		initialUnitat: unitat,
		initialPageSize: pageSize,
		// Controlled: the parent (GalleryContainer) owns pagination/sort;
		// the hook mirrors NOTHING — it fetches with these and reports
		// changes back through the same callbacks.
		page: page,
		pageSize: pageSize,
		sortField: initialSortField,
		sortDirection: initialSortDirection,
		onPageChange,
		minYear,
		maxYear,
		onSortFieldChange,
		onSortDirectionChange,
		// Pass showSecret as onlySecret if we are in secret tab
		onlySecret: showSecret,
		// Pass includeSecret from parent (when "Veure totes" is clicked) or effectiveShowSecret
		includeSecret: includeSecret ?? effectiveShowSecret,
		// Live pending-rotate preview: slot aspect reflows during the edit.
		pendingDeltas,
	});

	// Notify parent about loading state changes
	useEffect(() => {
		if (onIsLoadingChange) {
			onIsLoadingChange(isLoading);
		}
	}, [isLoading, onIsLoadingChange]);

	// State for managing which item is targeted by a mutation - REMOVED for pending changes
	// const [targetItemId, setTargetItemId] = useState<string | null>(null);
	// State for pending changes - MOVED TO PARENT
	// const [pendingChanges, setPendingChanges] = useState<PendingChangesMap>(new Map());

	useEffect(() => {
		if (onPageCountChange && pictureList) {
			onPageCountChange(pictureList.pageCount);
		}
	}, [pictureList, onPageCountChange]);

	// Combined rotation per item (persisted + pending delta) for layout
	// consumers (justified grid slots + lightbox). Pending rotate deltas are
	// absolute-increment values (±90) on top of the persisted field.
	const pendingRotations = useMemo(() => {
		const m = new Map<string, number>();
		if (!pendingChanges || !pictureList) return m;
		for (const [itemId, change] of pendingChanges.entries()) {
			if (change.action !== 'rotate') continue;
			const base =
				pictureList.mediaItems.find((x) => x._id === itemId)?.rotation ??
				0;
			const total =
				(((base + (change.value as number)) % 360) + 360) % 360;
			m.set(itemId, total);
		}
		return m;
	}, [pendingChanges, pictureList]);

	// Instantiate the lightbox hook, passing necessary state/handlers from useMediaGallery
	const {
		isLightboxOpen,
		currentImage,
		fullImages,
		openLightbox,
		closeLightbox,
		changeImage,
	} = useLightbox({
		pictureList,
		page,
		handlePageChange: internalHandlePageChange,
	});

	// Simplified handler for thumbnail clicks, calls function from useLightbox
	// Prevent opening lightbox when in edit mode and clicking overlay buttons (handled later)
	const handleThumbnailClick = (
		index: number,
		_item?: Image, // Mark item as unused with underscore
		event?: React.MouseEvent
	) => {
		// Check if the click target is one of the overlay buttons
		const target = event?.target as HTMLElement;
		if (isEditingPhotos && target?.closest('[data-edit-button="true"]')) {
			return; // Don't open lightbox if an edit button was clicked
		}
		openLightbox(index);
	};

	// --- Custom Thumbnail Component ---
	// Needs access to isEditingPhotos, pictureList, and handlers from the parent scope
	// Use the specific ThumbnailImageProps type from the library
	// --- Mutations ---
	const albumQueryKey = ['album', resolvedAlbumId];
	const albumsListQueryKey = ['albums', paramUnitat || 'tots']; // Invalidate unitat list or 'tots'

	// Bulk toggle secret mutation
	const bulkToggleMutation = useSimpleMutation<
		{ isSecret: boolean },
		{ modifiedCount: number }
	>({
		method: 'patch',
		url: albumId
			? `/api/galeria/gallery-items/album/${albumId}/toggle-all-secret`
			: '',
		dependencies: [albumId],
		// Use album prefix to invalidate all picture variants for this album
		// without blowing away unrelated albums' caches
		queriesToInvalidate: [
			['pictures', resolvedAlbumId ? `album${resolvedAlbumId}` : 'totes'],
			albumQueryKey,
			albumsListQueryKey,
		],
	});

	// --- Handlers for Edit Actions (Call parent callback) ---
	const handleDeleteClick = (itemId: string) => {
		// Call parent handler to mark for deletion
		onPendingChange(itemId, { action: 'delete' });
	};

	const handleRotateClick = (itemId: string, direction: 'left' | 'right') => {
		const currentChange = pendingChanges.get(itemId);

		// If marked for deletion, ignore rotation
		if (currentChange?.action === 'delete') {
			return;
		}

		// Pending value is a DELTA (0/90/180/270) on top of the persisted rotation
		const currentRotation =
			(currentChange?.action === 'rotate'
				? (currentChange.value as number)
				: 0) ?? 0;
		const rotationAmount = direction === 'right' ? 90 : -90;
		let newRotation =
			(((currentRotation + rotationAmount) % 360) + 360) % 360;

		if (newRotation === 0 && currentChange?.action === 'rotate') {
			// Delta returns to 0: the item ends at its persisted rotation,
			// so signal removal of the pending change
			onPendingChange(itemId, null);
		} else {
			// Signal the new rotation delta
			onPendingChange(itemId, { action: 'rotate', value: newRotation });
		}
	};

	const handleToggleSecretClick = (itemId: string, currentSecret: boolean) => {
		const currentChange = pendingChanges.get(itemId);
		if (currentChange?.action === 'delete') return;

		// Toggle logic:
		// If no pending change, invert currentSecret.
		// If pending change exists, check if it restores original state.
		const pendingValue =
			currentChange?.action === 'toggleSecret'
				? (currentChange.value as boolean)
				: undefined;

		// If undefined, it means no pending toggle. So we want to toggle current.
		// If defined, we are toggling back.
		const newValue =
			pendingValue !== undefined ? !pendingValue : !currentSecret;

		if (newValue === currentSecret) {
			// Back to original state
			onPendingChange(itemId, null);
		} else {
			onPendingChange(itemId, {
				action: 'toggleSecret',
				value: newValue,
			});
		}
	};

	// Stable context for the module-scope thumbnail component (identity is
	// stable; only the VALUE changes across renders).
	const thumbContextValue = useMemo<GalleryThumbContextValue>(() => {
		const deps: GalleryThumbContextValue = {
			mediaItems: pictureList?.mediaItems ?? [],
			pendingChanges: pendingChanges ?? new Map(),
			isEditingPhotos,
			resolvedAlbumId,
			onDeleteClick: handleDeleteClick,
			onRotateClick: handleRotateClick,
			onToggleSecretClick: handleToggleSecretClick,
		};
		return deps;
	}, [
		pictureList,
		pendingChanges,
		isEditingPhotos,
		resolvedAlbumId,
		handleDeleteClick,
		handleRotateClick,
		handleToggleSecretClick,
	]);

	// --- Bulk Toggle Secret ---
	// Helper to compute effective secret status accounting for pending changes
	const getEffectiveSecret = (item: { _id: string; isSecret?: boolean }): boolean => {
		const pending = pendingChanges.get(item._id);
		if (pending?.action === 'toggleSecret') return pending.value as boolean;
		return item.isSecret || false;
	};

	const effectiveSecretCount =
		pictureList?.mediaItems.filter(getEffectiveSecret).length ?? 0;
	const effectivePublicCount =
		(pictureList?.mediaItems.length ?? 0) - effectiveSecretCount;
	const allAreSecret =
		effectivePublicCount === 0 && effectiveSecretCount > 0;
	const allArePublic = effectiveSecretCount === 0 && effectivePublicCount > 0;

	const handleBulkToggleSecret = (makeSecret: boolean) => {
		const count = makeSecret ? effectivePublicCount : effectiveSecretCount;
		const label = makeSecret ? 'privades' : 'públiques';
		const action = makeSecret ? 'Fer' : 'Fer públiques';
		if (
			!window.confirm(`${action} ${count} fotos ${label}?`)
		)
			return;
		bulkToggleMutation.mutate({ isSecret: makeSecret });
	};

	// --- Render Logic ---

	// Determine if we should show the secret notice
	const hasSecretItems = (pictureList?.secretCount || 0) > 0;
	// Show notice if there are secret items and we are NOT in the dedicated secret tab (showSecret prop)
	const showSecretNotice = hasSecretItems && !showSecret;

	// Render loading state based on isLoading from useMediaGallery
	if (isLoading) {
		return (
			<LoadingMessage>
				<LoadingGif size="large" />
			</LoadingMessage>
		);
	}
	// Render error state based on error from useMediaGallery
	if (error) {
		return <ErrorMessage>Error: {extractErrorMessage(error)}</ErrorMessage>;
	}

	// Render message if pictureList is fetched but empty or null/undefined
	// (useMediaGallery returns null initially, then data or null on error/empty)
	// Allow rendering if we have a secret notice even if the public list is empty
	if (
		(!pictureList || pictureList.mediaItems.length === 0) &&
		!showSecretNotice
	) {
		// Check specifically for length 0 after confirming pictureList exists
		const message = !pictureList
			? "No s'han pogut carregar les fotos" // Error case or initial state
			: 'No hi ha fotos'; // Empty list case
		const MessageComponent = !pictureList
			? ErrorMessage
			: S.NoPicturesMessage;
		return <MessageComponent>{message}</MessageComponent>;
	}

	// Render gallery and lightbox if data is available
	return (
		<S.PicturesContainer>
			{showSecretNotice && (
				<S.SecretNoticeContainer>
					<span>
						Hi ha {pictureList?.secretCount} fotos amagades en
						aquest àlbum.
					</span>
					<S.SecretNoticeButton
						onClick={() => setLocalShowSecret(!localShowSecret)}
					>
						{localShowSecret ? 'Amagar fotos' : 'Veure fotos'}
					</S.SecretNoticeButton>
				</S.SecretNoticeContainer>
			)}

			{/* Bulk toggle-all bar */}
			{isEditingPhotos &&
				albumId &&
				pictureList &&
				pictureList.mediaItems.length > 0 && (
					<S.ToggleAllContainer>
						<span style={{ fontSize: '0.9rem', color: '#666' }}>
							{effectiveSecretCount} privades /{' '}
							{effectivePublicCount} públiques
						</span>
						<S.ToggleAllButton
							onClick={() => handleBulkToggleSecret(allArePublic)}
							disabled={bulkToggleMutation.isPending}
						>
							{bulkToggleMutation.isPending
								? 'Canviant...'
								: allAreSecret
								? 'Fer públiques totes'
								: 'Fer privades totes'}
						</S.ToggleAllButton>
					</S.ToggleAllContainer>
				)}

			{pictureList && pictureList.mediaItems.length > 0 && (
				<GalleryThumbContext.Provider value={thumbContextValue}>
					<Gallery
						images={thumbnailImages}
						enableImageSelection={false}
						onClick={handleThumbnailClick}
						rowHeight={180}
						margin={2}
						thumbnailImageComponent={GalleryCustomThumbnail}
					/>
				</GalleryThumbContext.Provider>
			)}

			{/* MediaLightbox uses state/handlers from useLightbox and mediaItems from useMediaGallery */}
			<MediaLightbox
				isOpen={isLightboxOpen}
				currentImage={currentImage}
				fullImages={fullImages}
				// Pending-aware: swap in the preview rotation so the lightbox
				// frames rotated crops/sizes live, before confirm.
				mediaItems={
					pictureList
						? pictureList.mediaItems.map((m) => {
							const pr = pendingRotations.get(m._id ?? '');
							return pr === undefined
								? m
								: { ...m, rotation: pr };
						  })
						: [] // Pass the media items safely
				}
				onClose={closeLightbox}
				onChangeImage={changeImage}
				page={currentPage} // Pass current page
				pageSize={currentPageSize} // Pass page size
				totalCount={pictureList ? pictureList.totalCount : 0} // Pass total count
				hideAlbumName={hideAlbumName} // Pass hideAlbumName
			/>
		</S.PicturesContainer>
	);
};

export default Pictures;
